import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const CONTACT_EMAIL = "Gallenhunt90@gmail.com";
const BUSINESS_NAME = "The Couponer";
const BUSINESS_LOCATION = "the United States";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy · The Couponer",
    description: "How The Couponer collects, uses, and protects your personal information.",
    canonical: "https://thecouponer.com/privacy",
  });

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="privacy-page">
      <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">LEGAL</div>
      <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight mb-6">Privacy Policy</h1>
      <p className="text-sm text-[#5c5c5c] mb-8">
        Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </p>

      <div className="max-w-none space-y-6 text-[15px] leading-relaxed">
        <p>
          {BUSINESS_NAME} (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) operates <strong>thecouponer.com</strong> (the &quot;Service&quot;), a coupon and deal aggregator. This Privacy Policy explains what personal information we collect, how we use it, who we share it with, and the choices you have. We aim to be plain-spoken here — if anything is unclear, email us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
        </p>

        <h2 className="serif text-2xl mt-8">1. Information we collect</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Account info</strong> — when you sign up, we store your email, display name, and (if you use Google) your profile picture URL. If you sign up with a password, we store a bcrypt hash, never the plain-text password.</li>
          <li><strong>Preferences</strong> — your ZIP code, saved coupons, digest opt-in status, and copy history so we can personalize the feed.</li>
          <li><strong>Usage data</strong> — pages visited, coupons clicked (for our own click-through metrics), IP address, browser user agent, referrer URL. Retained for up to 24 months for analytics and abuse prevention.</li>
          <li><strong>Payment info</strong> — if you upgrade to Pro or pay to sponsor a deal, payments are processed by <strong>Stripe</strong>. We never see or store full card numbers; Stripe returns a transaction ID which we associate with your account.</li>
          <li><strong>Cookies</strong> — a session cookie so you stay signed in, plus a local-storage entry for your saved ZIP and copy history. We do not use third-party ad-tracking cookies without your consent.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">2. How we use it</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Show and personalize the coupon feed</li>
          <li>Send transactional emails (welcome, password reset)</li>
          <li>Send the daily deals digest — only if you opt in</li>
          <li>Log outbound clicks so we can measure and prove CTR to sponsoring retailers</li>
          <li>Prevent abuse, spam, and brute-force attacks</li>
          <li>Respond to your support messages</li>
        </ul>

        <h2 className="serif text-2xl mt-8">3. Third-party services we use</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Stripe</strong> — payments. Privacy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener nofollow" className="underline">stripe.com/privacy</a></li>
          <li><strong>Resend</strong> — transactional and digest email delivery. Privacy: <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener nofollow" className="underline">resend.com/legal</a></li>
          <li><strong>Google OAuth</strong> — only if you sign in with Google. We receive email, name, and profile picture URL. Privacy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener nofollow" className="underline">policies.google.com/privacy</a></li>
          <li><strong>Skimlinks</strong> and <strong>Amazon Associates</strong> — affiliate networks that append tracking parameters to outbound retailer links so we earn a small commission on qualifying purchases. This costs you nothing extra.</li>
          <li><strong>Google AdSense</strong> — may show ads. Google may use cookies to personalize ads based on your visits here and other sites. Opt out at <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener nofollow" className="underline">google.com/settings/ads</a>.</li>
          <li><strong>logo.dev</strong> — CDN for retailer logos. No user data is sent to them.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">4. Sharing</h2>
        <p>
          We do <strong>not</strong> sell your personal information. We share only with the vendors above to run the Service, or if legally required (subpoena, court order). We may share aggregated, de-identified statistics (e.g. &quot;10,000 clicks on Nike deals last month&quot;) with retailers.
        </p>

        <h2 className="serif text-2xl mt-8">5. Your rights</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Access & delete</strong> — email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> and we&#39;ll export or delete your data within 30 days.</li>
          <li><strong>Unsubscribe</strong> from digest emails — one-click link at the bottom of every email.</li>
          <li><strong>California residents (CCPA)</strong> — right to know, delete, correct, and opt out of &quot;sale&quot; (we don&#39;t sell).</li>
          <li><strong>EU/UK residents (GDPR)</strong> — right to access, rectify, erase, restrict processing, and data portability. Legal basis for processing is your consent + our legitimate interest in running the Service.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">6. Security</h2>
        <p>
          Passwords are hashed with bcrypt (cost factor 12). Sessions are HttpOnly + Secure cookies. All traffic is served over HTTPS. We rate-limit login attempts to prevent brute-force. No system is bulletproof, but we take reasonable measures.
        </p>

        <h2 className="serif text-2xl mt-8">7. Data retention</h2>
        <p>Account data is kept until you delete your account. Click logs are kept up to 24 months. Payment records are kept 7 years (US tax law).</p>

        <h2 className="serif text-2xl mt-8">8. Children</h2>
        <p>The Service is not directed at children under 13 and we do not knowingly collect information from them.</p>

        <h2 className="serif text-2xl mt-8">9. Changes</h2>
        <p>We&#39;ll update the &quot;Last updated&quot; date above when this policy changes. Material changes will be announced via email or a banner on the site.</p>

        <h2 className="serif text-2xl mt-8">10. Contact</h2>
        <p>
          {BUSINESS_NAME} · Based in {BUSINESS_LOCATION}<br />
          Questions? <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
        </p>

        <p className="text-sm text-[#5c5c5c] mt-10">
          See also: <Link to="/terms" className="underline">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
